import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ib00ramli27834go3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] text-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-4xl">
              <h1 className="text-6xl font-bold mb-8">Contact Zeus Specialty Infusion Clinic</h1>
              <p className="text-2xl text-gray-100 leading-relaxed font-light">
                Our clinical coordination team is available to answer questions, schedule appointments, and provide comprehensive information about specialty infusion services throughout Los Angeles.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-[#0f1e32] mb-10">Get in Touch</h2>
                
                <div className="space-y-8 mb-12">
                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-map-pin-line text-3xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0f1e32] text-xl mb-2">Clinical Location</h3>
                      <p className="text-gray-600 text-lg">Los Angeles, California</p>
                      <p className="text-sm text-gray-500 mt-1">Serving the greater Los Angeles metropolitan area</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-phone-line text-3xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0f1e32] text-xl mb-2">Phone</h3>
                      <a href="tel:5551234567" className="text-gray-600 hover:text-[#0f1e32] transition-colors cursor-pointer text-lg">
                        (555) 123-4567
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Monday – Friday: 8:00 AM – 5:00 PM</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-mail-line text-3xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0f1e32] text-xl mb-2">Email</h3>
                      <a href="mailto:inquiries@zsicenter.com" className="text-gray-600 hover:text-[#0f1e32] transition-colors cursor-pointer text-lg">
                        inquiries@zsicenter.com
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Response within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className="ri-printer-line text-3xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0f1e32] text-xl mb-2">Secure Fax (Physician Referrals)</h3>
                      <p className="text-gray-600 text-lg">(555) 123-4568</p>
                      <p className="text-sm text-gray-500 mt-1">HIPAA-compliant fax line available 24/7</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h3 className="font-bold text-[#0f1e32] text-2xl mb-6">Clinical Hours</h3>
                  <div className="space-y-3 text-gray-600">
                    <div className="flex justify-between text-lg">
                      <span>Monday – Friday:</span>
                      <span className="font-semibold">8:00 AM – 5:00 PM</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span>Saturday:</span>
                      <span className="font-semibold">By Appointment</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span>Sunday:</span>
                      <span className="font-semibold">Closed</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 shadow-2xl border border-gray-200">
                  <h2 className="text-3xl font-bold text-[#0f1e32] mb-8">Send Us a Message</h2>
                  
                  <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-bold text-[#0f1e32] mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-base"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-bold text-[#0f1e32] mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-base"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-bold text-[#0f1e32] mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all text-base"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-bold text-[#0f1e32] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#d4af37] focus:border-transparent outline-none transition-all resize-none text-base"
                        placeholder="Tell us how we can help you..."
                      />
                      <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <i className="ri-check-line text-2xl"></i>
                          <span className="font-semibold">Thank you! Our team will contact you within 24 hours.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <i className="ri-error-warning-line text-2xl"></i>
                          <span className="font-semibold">Unable to send message. Please try again or call us directly.</span>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg" 
                      className="w-full !py-4 !text-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Sending Message...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-[#0f1e32] mb-6">Find Our Facility</h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Conveniently located to serve patients throughout the Los Angeles metropolitan area
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d423286.27404345275!2d-118.69192993092496!3d34.02016130653294!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c75ddc27da13%3A0xe22fdf6f254608f4!2sLos%20Angeles%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Zeus Specialty Infusion Clinic Location"
              ></iframe>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-5xl font-bold mb-8">Ready to Schedule Your Infusion?</h2>
            <p className="text-2xl text-gray-100 mb-12 leading-relaxed font-light">
              Our clinical coordination team is available to answer questions, verify insurance benefits, and schedule your initial infusion appointment with same-week availability.
            </p>
            <div className="flex flex-wrap justify-center gap-5">
              <a href="tel:5551234567">
                <Button variant="secondary" size="lg" className="!px-10 !py-4 !text-lg">
                  <i className="ri-phone-line mr-2"></i>
                  Call Now: (555) 123-4567
                </Button>
              </a>
              <a href="mailto:inquiries@zsicenter.com">
                <Button variant="outline" size="lg" className="!text-white !border-white hover:!bg-white hover:!text-[#0f1e32] !px-10 !py-4 !text-lg">
                  <i className="ri-mail-line mr-2"></i>
                  Email Our Team
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
